import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    PaymentMethodsEnum,
    TradingAccountStatusesEnum,
    ReportsService,
    AuthApiService,
    PaymentsService,
    IndividualsService,
    CashAccountDetailedReportInterface,
    TradingAccountDetailedReportInterface,
    LinkedAccountModel,
    TradingAccountModel,
    CashAccountDetailedFilterModel,
    CashAccountDetailedReportModel,
    TradingAccountDetailedFilterModel,
    TradingAccountDetailedReportModel
} from '@spa/api';

import {AlertTypesEnum} from '../../../../shared/enums';
import {FormComponent} from '../../../../shared/widgets';
import {AlertsService} from '../../../../shared/services';
import {FormInterface, PermissionRuleInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components';

import {DepositForm} from '../../forms';

import {DepositFormErrorStatesEnum} from '../../enums/error-states';

import {DepositMessages} from '../../../../../assets/messages';
import {PanelAccountsEditTrait} from 'appEntries/panel/traits';
import { MaxPaymentAmount } from '../../../../../assets/descriptors/panel-payments.descriptor';


@Component({
    selector: 'app-deposit',
    templateUrl: './deposit.component.html',
    styleUrls: ['./deposit.component.scss']
})
export class DepositComponent extends DialogComponent<DepositComponent, boolean>
    implements FormInterface, OnDestroy {

    private getAccountFromDetailsSubscription: Subscription;
    private registerDepositSubscription: Subscription;

    public paymentMethod: number;
    public depositForm: DepositForm;
    public visibleTransferButton: boolean;
    public paymentMethods: typeof PaymentMethodsEnum;
    public accountFrom: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;
    public allowedBankTransferAccountStatuses: number[];

    constructor(
        private readonly authenticationService: AuthApiService,
        private paymentsService: PaymentsService,
        private individualsService: IndividualsService,
        private reportsService: ReportsService,
        private alertService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            tradingAccounts: TradingAccountModel[],
            linkedAccounts: LinkedAccountModel[],
            config: DialogConfigInterface,
            transferPermissionRule: PermissionRuleInterface,
            depositPermissionRule: PermissionRuleInterface
        },
        public dialogRef: MatDialogRef<DepositComponent>
    ) {
        super(dialogRef);

        this.config = data.config;
        this.paymentMethods = PaymentMethodsEnum;

        this.visibleTransferButton = PanelAccountsEditTrait.checkVisibilityTransferButton(
            this.data.account.AccountSubType,
            this.authenticationService.getAuthenticatedUser(),
            this.data.transferPermissionRule
        );

        this.allowedBankTransferAccountStatuses = [TradingAccountStatusesEnum.Active, TradingAccountStatusesEnum.AwaitingFunding, TradingAccountStatusesEnum.WithdrawalRestricted];
    }

    private getAccountRequestSubscriber(
        isCashAccount: boolean,
        refreshCurrent: boolean,
        response: ServerResponseData<CashAccountDetailedReportInterface | TradingAccountDetailedReportInterface>
    ): boolean {
        if ((response.status !== HttpStatusCodesEnum.Ok) || (response.data === null)) {
            this.alertService.addAlert(DepositMessages.GetAccountFailed.message, AlertTypesEnum.Error);

            return false;
        }

        const data = response.data;

        const model = !isCashAccount ?
            new TradingAccountDetailedReportModel(data as TradingAccountDetailedReportInterface) :
            new CashAccountDetailedReportModel(data as CashAccountDetailedReportInterface);

        if (!refreshCurrent) {
            this.accountFrom = model;
        } else {
            if (!this.data.account.isCashAccount) {
                (this.data.account as TradingAccountDetailedReportModel).availableCash =
                    (model as TradingAccountDetailedReportModel).availableCash;
            } else {
                (this.data.account as CashAccountDetailedReportModel).availableCash =
                    (model as CashAccountDetailedReportModel).availableCash;
            }
        }

    }

    selectPaymentMethod(method: number): void {
        this.initForm(method);

        if ((method === PaymentMethodsEnum.InternalTransfer) && ([0, 1].indexOf(this.data.tradingAccounts.length) < 0)) {
            this.paymentMethod = method;
            this.data.config.buttonsHidden = false;
        }

        if (
            (method === PaymentMethodsEnum.BankTransfer) &&
            (this.data.linkedAccounts.length > 0) &&
            (this.allowedBankTransferAccountStatuses.indexOf(this.data.account.accountStatus) >= 0)
        ) {
            this.paymentMethod = method;
            this.data.config.buttonsHidden = false;
        }
    }

    ngOnDestroy(): void {
        if (this.getAccountFromDetailsSubscription) {
            this.getAccountFromDetailsSubscription.unsubscribe();
        }

        if (this.registerDepositSubscription) {
            this.registerDepositSubscription.unsubscribe();
        }
    }

    getAccountDetails(accountId: number, refreshCurrent?: boolean): void {
        if (accountId) {
            let isCashAccount = false;
            let accountReferenceId = '';

            if (!refreshCurrent) {
                const filteredAccounts = this.data.tradingAccounts.filter((account: TradingAccountModel) => {
                    return account.id === accountId;
                });

                if (filteredAccounts.length > 0) {
                    isCashAccount = filteredAccounts[0].isCashAccount;
                    accountReferenceId = filteredAccounts[0].accountReferenceId;
                }
            } else {
                isCashAccount = this.data.account.isCashAccount;
                accountReferenceId = this.data.account.accountReferenceId;
            }

            if (accountReferenceId) {
                let filter = null;
                let request = null;

                if (!isCashAccount) {
                    filter = new TradingAccountDetailedFilterModel(null, accountReferenceId);
                    request = this.reportsService.getTradingAccountDetailedReport(filter);
                } else {
                    filter = new CashAccountDetailedFilterModel(null, accountReferenceId);
                    request = this.reportsService.getCashAccountDetailedReport(filter);
                }

                this.getAccountFromDetailsSubscription = request
                    .subscribe(this.getAccountRequestSubscriber.bind(this, isCashAccount, refreshCurrent));
            }
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        const unavailableCashMessage = DepositMessages.UnavailableAccountFromCash.message;
        const notEnoughMoneyMessage = DepositMessages.AccountFromNotEnoughMoney.message;

        return {
            ...FormComponent.getFormMessages(controlLabel, controlMaxLength),
            [DepositMessages.UnavailableAccountFromCash.key]: unavailableCashMessage,
            [DepositMessages.AccountFromNotEnoughMoney.key]: notEnoughMoneyMessage
        };
    }

    initForm(method?: number): void {
        this.depositForm = new DepositForm(
            this.data.account,
            this.data.linkedAccounts,
            this.data.tradingAccounts,
            this.paymentsService,
            this.individualsService
        );

        this.depositForm.initForm(method);
    }

    validateForm(): boolean {
        return this.depositForm.validateForm(
            this.getFormMessages.bind(this),
            this.paymentMethod,
            this.accountFrom,
            DepositMessages.UnavailableAccountFromCash.key,
            DepositMessages.AccountFromNotEnoughMoney.key,
            MaxPaymentAmount
        );
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.registerDepositSubscription = this.depositForm.createRequest(this.paymentMethod)
                            .subscribe((response: ApiResponse<ApiError[] | boolean>) => {
                                if (response.status !== HttpStatusCodesEnum.Ok) {
                                    this.config.process.failedMessage = (response.data as ApiError[])
                                        .map((error: ApiError) => {
                                            switch (error.status) {
                                                case DepositFormErrorStatesEnum.GetClientFailed:
                                                    return DepositMessages.GetClientFailed.message;
                                                case DepositFormErrorStatesEnum.RegisterBankDepositFailed:
                                                    return DepositMessages.RegisterBankDepositFailed.message;
                                                case DepositFormErrorStatesEnum.RegisterInternalDepositFailed:
                                                    return DepositMessages.RegisterInternalDepositFailed.message;
                                                case DepositFormErrorStatesEnum.CanFindCorrectPaymentMethod:
                                                    return DepositMessages.CanFindCorrectPaymentMethod.message;
                                                case DepositFormErrorStatesEnum.GetDefaultVendorBankAccountFailed:
                                                    return DepositMessages.GetDefaultVendorBankAccountFailed.message;
                                                case DepositFormErrorStatesEnum.DefaultVendorBankAccountNotFound:
                                                    return DepositMessages.DefaultVendorBankAccountNotFound.message;
                                            }
                                        }).join('<br>');

                                    this.config.process.failed = true;
                                } else {
                                    switch (this.paymentMethod) {
                                        case PaymentMethodsEnum.BankTransfer:
                                            this.config.process.succeedMessage =
                                                DepositMessages.RegisterBankDepositSucceed.message;
                                            break;
                                        case PaymentMethodsEnum.InternalTransfer:
                                            this.config.process.succeedMessage =
                                                DepositMessages.RegisterInternalDepositSucceed.message;
                                            break;
                                    }
                                }

                                this.config.process.loading = false;
                                this.config.process.finished = true;
                            });
                    });
                }
                break;
        }
    }
}
